import React, { useState, useEffect, useCallback } from "react";
import { Drawer, Avatar, Typography, Spin, Tooltip } from "antd";
import {
  TeamOutlined, CrownFilled,
  StarFilled, StarOutlined, UserSwitchOutlined,CheckCircleFilled,SwapOutlined 
} from "@ant-design/icons";
import axios from "axios";
import toast from "react-hot-toast";
import {
  G700, G100, G50, GRAY6, GRAY7, GRAY2,
  SLATE, MUTED, WHITE,
  GRAD_BRAND,
  RADIUS_SM, RADIUS_MD, RADIUS_PILL,
  SHADOW_BLUE,
  FF_DISPLAY, FF_BODY, FF_MONO,
  TRANSITION_SM,SHADOW_SM,GRAY4,G500
} from "./patientTokens";
 
const { Text } = Typography;
const PHP_APP_URL = process.env.REACT_APP_PHP_URL || "http://localhost:92";
 
const AMBER_STAR = "#DD8D2A";
const STAR_BG    = "#FFFBF0";
 
const initialsOf = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join("") || "P";
 
const photoOf = (dep) =>
  dep?.photo ? `${PHP_APP_URL}/uploads/patients/img/${dep.photo}` : undefined;

function initials(name = "") {
  return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "?";
}

function ageLabel(dob) {
  if (!dob) return null;
  const years = Math.floor((Date.now() - new Date(dob)) / 31557600000);
  return years < 1 ? "< 1 yr" : `${years} yr${years !== 1 ? "s" : ""}`;
}


 // ═════ 1) REPLACEMENT DependentCard ═══════════════════════════════
function DependentCard({ dep, isActive, onSelect, starSlot }) {
  const [hovered, setHovered] = useState(false);
  const age = ageLabel(dep.date_of_birth);
 
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(dep)}
      onKeyDown={(e) => e.key === "Enter" && onSelect(dep)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display:      "flex",
        alignItems:   "center",
        gap:          14,
        width:        "100%",
        textAlign:    "left",
        background:   isActive
          ? `linear-gradient(135deg, ${G700}, ${G500 || G700})`
          : hovered ? G50 : WHITE,
        border:       isActive
          ? "1.5px solid transparent"
          : `1.5px solid ${hovered ? G100 : GRAY6}`,
        borderRadius: RADIUS_MD,
        padding:      "13px 14px 13px 16px",
        cursor:       "pointer",
        transition:   TRANSITION_SM,
        boxShadow:    isActive ? SHADOW_BLUE : hovered ? SHADOW_SM : "none",
        position:     "relative",
        overflow:     "hidden",
        minHeight:    72,
        marginBottom: 8,
      }}
    >
      {/* Active shimmer strip */}
      {isActive && (
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0,
          width: 4,
          background: "rgba(255,255,255,0.5)",
          borderRadius: "3px 0 0 3px",
        }} />
      )}
 
      {/* Avatar */}
      <div style={{ position: "relative", flexShrink: 0 }}>
        <Avatar
          size={44}
          src={photoOf(dep)}                     /* ← was photoOf(dep.photo) */
          style={{
            background: isActive
              ? "rgba(255,255,255,0.25)"
              : GRAD_BRAND,
            fontSize:   16,
            fontWeight: 700,
            border:     isActive ? "2px solid rgba(255,255,255,0.5)" : "none",
          }}
        >
          {initials(dep.full_name)}
        </Avatar>
        {isActive && (
          <CheckCircleFilled style={{
            position:  "absolute", bottom: -2, right: -2,
            color:     "#4ade80", fontSize: 15,
            background: WHITE, borderRadius: "50%",
          }} />
        )}
      </div>
 
      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text style={{
          fontSize:      13.5,
          fontWeight:    700,
          color:         isActive ? WHITE : SLATE,
          display:       "block",
          letterSpacing: "-0.2px",
          whiteSpace:    "nowrap",
          overflow:      "hidden",
          textOverflow:  "ellipsis",
          fontFamily:    FF_DISPLAY,
        }}>
          {dep.full_name}
          {dep.is_primary && (
            <span style={{
              fontSize: 9, fontWeight: 700,
              marginLeft: 7, padding: "1px 6px",
              borderRadius: 20,
              background: isActive ? "rgba(255,255,255,0.2)" : G100,
              color:      isActive ? WHITE : G700,
              verticalAlign: "middle",
            }}>
              YOU
            </span>
          )}
        </Text>
        <Text style={{
          fontSize:   11,
          color:      isActive ? "rgba(255,255,255,0.65)" : MUTED,
          fontFamily: FF_MONO,
          display:    "block",
          marginTop:  2,
        }}>
          {dep.prn}
        </Text>
        <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
          {dep.gender && (
            <span style={{
              fontSize: 9.5, fontWeight: 600,
              padding: "1px 7px", borderRadius: 20,
              background: isActive ? "rgba(255,255,255,0.15)" : GRAY7,
              color:      isActive ? "rgba(255,255,255,0.8)" : MUTED,
            }}>
              {dep.gender}
            </span>
          )}
          {age && (
            <span style={{
              fontSize: 9.5, fontWeight: 600,
              padding: "1px 7px", borderRadius: 20,
              background: isActive ? "rgba(255,255,255,0.15)" : GRAY7,
              color:      isActive ? "rgba(255,255,255,0.8)" : MUTED,
            }}>
              {age}
            </span>
          )}
          {dep.blood_group && (
            <span style={{
              fontSize: 9.5, fontWeight: 700,
              padding: "1px 7px", borderRadius: 20,
              background: isActive ? "rgba(255,255,255,0.15)" : "#fff0f0",
              color:      isActive ? "rgba(255,255,255,0.9)" : "#ef4444",
            }}>
              {dep.blood_group}
            </span>
          )}
        </div>
      </div>
 
      {/* Arrow (only when not active) */}
      {!isActive && (
        <SwapOutlined style={{ color: GRAY4, fontSize: 13, flexShrink: 0 }} />
      )}
 
      {/* ★ default-pin button, injected by the drawer */}
      {starSlot}
    </div>
  );
}
 


export function useSetDefaultPatient(token, onChanged) {
  const [savingPrn, setSavingPrn] = useState(null);
 
  const setDefault = useCallback(async (dep) => {
    if (savingPrn) return;
    setSavingPrn(dep?.prn || "self");
    try {
      const res = await axios.put(
        "/api/patient/default-patient",
        { prn: dep && !dep.is_primary ? dep.prn : null },  // primary/null = reset
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success(res.data.message);
        onChanged?.(res.data.defaultPrn);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update default profile");
    } finally {
      setSavingPrn(null);
    }
  }, [token, savingPrn, onChanged]);
 
  return { setDefault, savingPrn };
}

// ── Avatar rail (compact, shown in sidebar) ────────────────────
export function DependentAvatarRail({ dependents, activePrn, onSelect, collapsed }) {
  if (!dependents || dependents.length <= 1) return null;
 
  return (
    <div style={{ width: "100%" }}>
      {!collapsed && (
        <Text style={{
          color: MUTED, fontSize: 9, fontWeight: 700,
          letterSpacing: "1px", textTransform: "uppercase",
          fontFamily: FF_BODY, display: "block", padding: "0 2px 6px",
        }}>
          Family
        </Text>
      )}
      <div style={{
        display: "flex",
        flexDirection: collapsed ? "column" : "row",
        alignItems: "center",
        gap: 8,
        flexWrap: collapsed ? "nowrap" : "wrap",
        justifyContent: collapsed ? "center" : "flex-start",
      }}>
        {dependents.map(dep => {
          const isActive = dep.prn === activePrn;
          return (
            <Tooltip
              key={dep.prn}
              title={`${dep.full_name}${dep.is_primary ? " (Primary)" : ""}${dep.is_default ? " · default" : ""}`}
              placement="right"
            >
              <button
                onClick={() => onSelect(dep)}
                aria-label={`Switch to ${dep.full_name}`}
                style={{
                  position: "relative",
                  width: 38, height: 38, padding: 0,
                  border: "none", borderRadius: "50%",
                  background: "transparent", cursor: "pointer",
                  outlineOffset: 2, flexShrink: 0,
                  transform: isActive ? "scale(1.05)" : "none",
                  transition: TRANSITION_SM,
                }}
              >
                <Avatar
                  size={36}
                  src={photoOf(dep)}
                  style={{
                    background: isActive ? GRAD_BRAND : GRAY7,
                    color: isActive ? WHITE : MUTED,
                    fontSize: 12, fontWeight: 700,
                    border: isActive ? `2px solid ${G700}` : `2px solid ${GRAY6}`,
                    boxShadow: isActive ? SHADOW_BLUE : "none",
                  }}
                >
                  {initialsOf(dep.full_name)}
                </Avatar>
 
                {/* Primary crown */}
                {dep.is_primary && (
                  <span style={{
                    position: "absolute", top: -3, right: -3,
                    width: 14, height: 14, borderRadius: "50%",
                    background: WHITE, border: `1px solid ${GRAY6}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 8, color: AMBER_STAR,
                  }}>
                    <CrownFilled />
                  </span>
                )}
 
                {/* Default ★ badge */}
                {dep.is_default && !dep.is_primary && (
                  <span style={{
                    position: "absolute", bottom: -3, right: -3,
                    width: 14, height: 14, borderRadius: "50%",
                    background: STAR_BG, border: `1px solid ${AMBER_STAR}55`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 8, color: AMBER_STAR,
                  }}>
                    <StarFilled />
                  </span>
                )}
              </button>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}


export function DefaultStarButton({ dep, savingPrn, onSetDefault }) {
  const busy = savingPrn === dep.prn;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onSetDefault(dep); }}
      disabled={busy}
      title={dep.is_default
        ? (dep.is_primary
            ? "Your records load first at sign-in"
            : "This profile loads first at sign-in")
        : (dep.is_primary
            ? "Reset: open your own records at sign-in"
            : "Open this profile first at sign-in")}
      aria-label={dep.is_default ? "Default profile" : "Set as default profile"}
      style={{
        width: 30, height: 30, flexShrink: 0,
        border: "none", borderRadius: 8, cursor: "pointer",
        background: dep.is_default ? STAR_BG : "transparent",
        color: dep.is_default ? AMBER_STAR : "#C5D2DF",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 14, transition: "color 0.15s, background 0.15s",
        opacity: busy ? 0.45 : 1,
      }}
      onMouseEnter={(e) => { if (!dep.is_default) e.currentTarget.style.color = AMBER_STAR; }}
      onMouseLeave={(e) => { if (!dep.is_default) e.currentTarget.style.color = "#C5D2DF"; }}
    >
      {dep.is_default ? <StarFilled /> : <StarOutlined />}
    </button>
  );
}

// ── Full dependent drawer ──────────────────────────────────────
export function DependentDrawer({
  visible, onClose, dependents, activePrn, onSelect, loading,
  token, onDefaultChanged,        // ← two new props (see wiring below)
}) {
  const { setDefault, savingPrn } = useSetDefaultPatient(token, onDefaultChanged);
 
  return (
    <Drawer
      open={visible}
      onClose={onClose}
      width={Math.min(360, typeof window !== "undefined" ? window.innerWidth - 24 : 360)}
      styles={{ body: { padding: 0 }, header: { display: "none" } }}
    >
      {/* Header */}
      <div style={{
        padding: "16px 18px 14px", borderBottom: `1px solid ${GRAY7}`,
        display: "flex", alignItems: "center", gap: 11,
      }}>
        <span style={{
          width: 34, height: 34, borderRadius: 10, flexShrink: 0,
          background: GRAD_BRAND, boxShadow: SHADOW_BLUE,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          color: WHITE, fontSize: 15,
        }}>
          <TeamOutlined />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{
            color: SLATE, fontSize: 14.5, display: "block",
            fontFamily: FF_DISPLAY, letterSpacing: "-0.3px",
          }}>
            Family Members
          </Text>
          <Text style={{ color: MUTED, fontSize: 11.5, fontFamily: FF_BODY }}>
            Registered on your phone number
          </Text>
        </div>
      </div>
 
      {/* Rows */}
   <div style={{ padding: 10 }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}><Spin /></div>
        ) : (
          dependents.map(dep => (
            <DependentCard
              key={dep.prn}
              dep={dep}
              isActive={dep.prn === activePrn}
              onSelect={(d) => { onSelect(d); onClose(); }}
              starSlot={
                <DefaultStarButton
                  dep={dep}
                  savingPrn={savingPrn}
                  onSetDefault={setDefault}
                />
              }
            />
          ))
        )}
      </div>
 
      {/* Footer hint */}
      <div style={{
        padding: "9px 16px", borderTop: `1px solid ${GRAY7}`, background: "#FAFCFF",
      }}>
        <Text style={{ fontSize: 10.5, color: MUTED, fontFamily: FF_BODY, lineHeight: 1.5 }}>
          Tap a member to view their records now. ★ chooses who loads first
          at sign-in — starring the primary resets it.
        </Text>
      </div>
    </Drawer>
  );
}
 

// ── Topbar switcher button (shown when dependents exist) ────────
export function DependentSwitcherBtn({ dependents, activePrn, onClick }) {
  if (!dependents || dependents.length <= 1) return null;
  const active = dependents.find(d => d.prn === activePrn) || dependents[0];
 
  return (
    <Tooltip title="Switch family member" placement="bottom">
      <button
        onClick={onClick}
        aria-label="Switch family member"
        style={{
          height: 40, padding: "0 12px 0 6px",
          borderRadius: RADIUS_SM,
          background: GRAY7, border: "1px solid transparent",
          display: "inline-flex", alignItems: "center", gap: 8,
          color: GRAY2, transition: TRANSITION_SM, cursor: "pointer",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = G50;
          e.currentTarget.style.borderColor = G100;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = GRAY7;
          e.currentTarget.style.borderColor = "transparent";
        }}
      >
        <Avatar
          size={28}
          src={photoOf(active)}
          style={{
            background: GRAD_BRAND, fontSize: 10.5,
            fontWeight: 700, flexShrink: 0,
          }}
        >
          {initialsOf(active?.full_name)}
        </Avatar>
        <UserSwitchOutlined style={{ fontSize: 14, color: G700 }} />
        <span style={{
          fontSize: 10, fontWeight: 700, color: G700, fontFamily: FF_MONO,
          background: G50, border: `1px solid ${G100}`,
          borderRadius: RADIUS_PILL, padding: "0 6px",
        }}>
          {dependents.length}
        </span>
      </button>
    </Tooltip>
  );
}

// ── Hook: load dependents once ─────────────────────────────────
export function useDependents(token) {
  const [dependents, setDependents] = useState([]);
  const [defaultPrn, setDefaultPrn] = useState(null);
  const [loading,    setLoading]    = useState(true);
 
  const refetch = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await axios.get("/api/patient/dependents", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setDependents(res.data.data || []);
        setDefaultPrn(res.data.defaultPrn || null);
      }
    } catch {
      /* silent — switcher simply won't render */
    } finally {
      setLoading(false);
    }
  }, [token]);
 
  useEffect(() => { refetch(); }, [refetch]);
 
  return { dependents, loading, refetch, defaultPrn };
}
 
export default DependentDrawer;